import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Box, Button, Divider, Flex, Heading, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { FiArrowRight, FiCheckSquare, FiClock, FiGlobe, FiUsers } from "react-icons/fi";
import { GET_PUBLIC_GOV_MOTION } from "../queries";
import { GET_TENANT_AUTH_CONFIG } from "../../authentication/queries";
import type { TenantAuthConfig } from "../../../generated/graphql";
import { GovMarkdown } from "./GovMarkdown";
import { GovProposalTeaser } from "./GovProposalTeaser";
import { GovSurface, GovStatusPill, GovLoading, FadeIn, useGovTheme, alpha } from "./ui";
import { GovTallyBar } from "./ui/GovTallyBar";

/**
 * What a forwarded motion link shows somebody who is not in the group (2026-09-16).
 *
 * A gate that says only "sign in to continue" converts worse than no gate at all — a stranger has no
 * idea what they are being asked to sign in FOR. So a group can choose to publish the whole motion:
 * the case as written, the ballot, and where the vote stands. Then the ask makes sense: join, and you
 * get a say in this.
 *
 * ── The rules this file keeps (seamless-page-auth-pattern.md § Part 3) ───────
 *  • **WRITTEN, never CLIPPED.** It imports no page component and no protected query. Everything here
 *    comes from `publicGovMotion`, a whitelisted public read the group switches on deliberately.
 *  • **Never the people.** Individual votes — who voted, and why — and the papers on the motion are
 *    not in that whitelist and never reach this page. The count is an aggregate.
 *  • When the group publishes only the short teaser, this falls back to `GovProposalTeaser`, so one
 *    component answers the route at every level.
 */
export const GovMotionPublic: React.FC<{ textPrimary?: string; textSecondary?: string }> = ({ textPrimary, textSecondary }) => {
  const { id = "" } = useParams<{ id: string }>();
  const t = useGovTheme();

  const motionQ = useQuery(GET_PUBLIC_GOV_MOTION, { variables: { id }, skip: !id, fetchPolicy: "network-only" });
  const authQ = useQuery<{ tenantAuthConfig: TenantAuthConfig | null }>(GET_TENANT_AUTH_CONFIG, { fetchPolicy: "cache-first" });
  const signupOpen = Boolean(authQ.data?.tenantAuthConfig?.allowEmailLinkSignup);

  const motion = motionQ.data?.publicGovMotion ?? null;
  const heading = textPrimary || t.text;
  const body = textSecondary || t.muted;

  if (motionQ.loading && !motionQ.data) return <GovLoading label="Opening the motion…" />;
  // The group publishes less than the whole motion: the short teaser answers the same link.
  if (!motion) return <GovProposalTeaser textPrimary={textPrimary} textSecondary={textSecondary} />;

  const leading = [...(motion.choices || [])].sort((a: any, b: any) => b.votes - a.votes)[0];

  return (
    <VStack align="stretch" spacing={4} textAlign="left" w="100%" maxW="42rem" mx="auto">
      <FadeIn>
        <GovSurface>
          <HStack spacing={3} align="start" flexWrap="wrap" rowGap={2} minW={0} w="100%">
            <Flex flexShrink={0} w={12} h={12} borderRadius="xl" bgImage={t.accentGradient} align="center" justify="center"
              color="white" fontWeight="bold">
              {motion.organisationName.split(/\s+/).filter(Boolean).slice(0, 2).map((w: string) => w[0]?.toUpperCase()).join("")}
            </Flex>
            <Box minW={0} flex="1">
              <Text fontSize="md" fontWeight="semibold" color={t.muted}>{motion.organisationName}</Text>
              {motion.organisationPurpose && (
                <Text fontSize="sm" color={t.muted} noOfLines={2}>{motion.organisationPurpose}</Text>
              )}
            </Box>
          </HStack>

          <Heading size="lg" mt={4} letterSpacing="-0.01em" color={heading} wordBreak="break-word">{motion.title}</Heading>

          <HStack spacing={2} mt={3} flexWrap="wrap" rowGap={2}>
            <GovStatusPill status={motion.status} />
            <Text fontSize="md" color={t.text}>{motion.statusWords}</Text>
            {motion.closesAtLabel && (
              <HStack spacing={1.5} color={t.muted} fontSize="md">
                <Icon as={FiClock} boxSize={4} />
                <Text>closes {motion.closesAtLabel}</Text>
              </HStack>
            )}
          </HStack>

          {motion.body && (
            <Box mt={4} fontSize="md" lineHeight="tall" color={body} maxW="72ch" sx={{ overflowWrap: "anywhere" }}>
              <GovMarkdown fontSize="md">{motion.body}</GovMarkdown>
            </Box>
          )}
        </GovSurface>
      </FadeIn>

      <FadeIn delay={0.05}>
        <GovSurface>
          <HStack spacing={2} mb={1}>
            <Icon as={FiCheckSquare} color={t.primary} />
            <Text fontSize="lg" fontWeight="bold" color={heading}>What is being voted on</Text>
          </HStack>
          <Text fontSize="md" color={body} mb={4}>{motion.passRule}</Text>

          <VStack align="stretch" spacing={3}>
            {(motion.choices || []).map((c: any) => (
              <Box key={c.key}>
                <Box mt={1}>
                  <GovTallyBar
                    label={c.label}
                    meta={`${c.votes} ${c.votes === 1 ? "vote" : "votes"}`}
                    percent={c.percentOfCast}
                    leading={leading?.key === c.key}
                    final={motion.statusWords !== "Voting is open"}
                  />
                </Box>
              </Box>
            ))}
          </VStack>

          <Divider my={4} borderColor={t.border} />
          <HStack spacing={2} color={t.muted} fontSize="md" flexWrap="wrap" rowGap={1}>
            <Icon as={FiUsers} />
            <Text>
              {motion.totalVotesCast} of {motion.membersCount} {motion.membersCount === 1 ? "member has" : "members have"} voted
              {motion.quorumMet ? " — enough for the result to count." : "."}
            </Text>
          </HStack>
          <Text fontSize="sm" color={t.faint} mt={2}>
            Who voted, and why, is private to the group. So are the papers attached to this motion.
          </Text>
        </GovSurface>
      </FadeIn>

      <FadeIn delay={0.1}>
        <GovSurface bg={alpha(t.primary, 0.06)} borderColor={alpha(t.primary, 0.3)}>
          <Text fontSize="lg" fontWeight="bold" color={heading}>Want a say in this?</Text>
          <Text fontSize="md" color={body} mt={1}>
            {motion.membersCount} {motion.membersCount === 1 ? "person decides" : "people decide"} this. Join {motion.organisationName} and
            you vote on this and everything after it.
            {signupOpen ? " You can create an account free when you sign in." : ""}
          </Text>
          <HStack mt={4} spacing={3} flexWrap="wrap" rowGap={2}>
            {/* Sign in happens on this page; the join page is where they land afterwards. */}
            <Button as={RouterLink} to={`/governance/${motion.organisationId}/join`} size="lg" minH="48px"
              bgImage={t.accentGradient} color="white" _hover={{ filter: "brightness(1.08)" }}
              rightIcon={<Icon as={FiArrowRight} />}>
              Join this group to vote
            </Button>
            {motion.publicPageEnabled && (
              <Button as={RouterLink} to={`/org/${motion.organisationSlug}`} size="md" minH="44px" variant="outline"
                leftIcon={<Icon as={FiGlobe} />}>
                About {motion.organisationName}
              </Button>
            )}
          </HStack>
        </GovSurface>
      </FadeIn>
    </VStack>
  );
};

export default GovMotionPublic;
